import { InitiativeStatus } from '../../../api/initiative-client/initiative-client.type';
import type { Team } from '../../../api/projects-client/projects-client.type';
import { icons } from '../../../assets/icons/icons';
import { primaryBase, redBase, skyBase, yellowBase } from '../../../styles/design-tokens';
import { iconsIntegrations } from '../../integrations/assets';
import type { StrategyEntity } from '../strategy.type';

/**
 * Returns the appropriate color for a status
 *
 * @param {string} status - The status string
 * @returns {string} The color code corresponding to the status
 */
const getStatusColor = (status: string | undefined): string => {
  switch (status) {
    case InitiativeStatus.OnTrack:
    case 'on_track':
      return primaryBase;
    case InitiativeStatus.AtRisk:
    case 'at_risk':
      return yellowBase;
    case InitiativeStatus.OffTrack:
    case 'off_track':
      return redBase;
    case InitiativeStatus.Unknown:
    case 'unknown':
      return skyBase;
    default:
      return skyBase;
  }
};

/**
 * Returns the appropriate icon for an initiative source
 *
 * @param {string} source - The source of the entity
 * @returns {string} The icon path corresponding to the source
 */
const getSourceIcon = (source: string): string => {
  const sourceIcons: Record<string, string> = {
    bloomfilter: icons.iconBloomfilter,
    jira: iconsIntegrations.iconJiraCloud,
    jira_data_center: iconsIntegrations.iconJiraDataCenter,
    ado: iconsIntegrations.iconADO,
    github: icons.iconGithub,
  };

  return sourceIcons[source] || icons.iconBloomfilter;
};

/**
 * Extracts the numeric portion from an entity key (e.g., "OBJ-10" -> 10)
 *
 * @param {string} key - The entity key
 * @returns {number} The numeric portion of the key, or 0 if not found
 */
const extractNumericId = (key: string): number => {
  const match = key.match(/\d+$/);
  return match ? parseInt(match[0], 10) : 0;
};

/**
 * Removes emojis from a string for sorting purposes
 *
 * @param {string} text - The text to strip emojis from
 * @returns {string} The text without emojis
 */
const stripEmojis = (text: string): string => {
  // Remove emojis using Unicode ranges for emoji characters
  return text
    .replace(
      /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F700}-\u{1F77F}]|[\u{1F780}-\u{1F7FF}]|[\u{1F800}-\u{1F8FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F191}-\u{1F251}]|[\u{1F004}]|[\u{1F0CF}]|[\u{1F170}-\u{1F171}]|[\u{1F17E}-\u{1F17F}]|[\u{1F18E}]|[\u{3030}]|[\u{2B50}]|[\u{2B55}]|[\u{2934}-\u{2935}]|[\u{2B05}-\u{2B07}]|[\u{2B1B}-\u{2B1C}]|[\u{3297}]|[\u{3299}]|[\u{303D}]|[\u{00A9}]|[\u{00AE}]|[\u{2122}]|[\u{23F0}-\u{23F3}]|[\u{23F8}-\u{23FA}]|[\u{FE0F}]/gu,
      '',
    )
    .trim();
};

/**
 * Resolves the display names of the teams contributing to a strategy entity.
 *
 * Prefers the server-resolved `teams` array that the outcomes list payload carries, and
 * falls back to deriving team ids from `contributing_teams_over_time` and looking their
 * names up in the caller-supplied list for entity shapes that predate that field.
 *
 * Server-resolved names are returned unfiltered: the API resolves them itself, so a blank
 * one means the payload is wrong and should be visible rather than silently reducing the
 * list. Only the fallback path drops unresolvable ids, because a local lookup legitimately
 * misses teams outside the current portfolio.
 *
 * Lives here, beside the sort, so the Teams column's rendered value and its sort key are
 * derived by one function and cannot drift apart.
 *
 * @param {StrategyEntity} entity - The row being rendered or compared
 * @param {Team[]} teams - Team list consulted only by the fallback id-lookup path
 * @returns {string[]} Team display names; empty when the entity has no contributing teams
 */
const getEntityTeamNames = (entity: StrategyEntity, teams: Team[] = []): string[] => {
  if ('teams' in entity && Array.isArray(entity.teams)) {
    return entity.teams.map((team) => team.name);
  }

  if ('contributing_teams_over_time' in entity && entity.contributing_teams_over_time) {
    return Object.keys(entity.contributing_teams_over_time)
      .map((id) => teams.find((team) => team.id === id)?.name)
      .filter((name): name is string => Boolean(name));
  }

  return [];
};

/**
 * Builds the string used to sort the Teams column.
 *
 * Derives from `getEntityTeamNames` so the sort always agrees with what the cell renders,
 * then falls back to the `contributing_teams_label` that the Initiatives pipeline
 * pre-computes. Lowercased for case-insensitive ordering.
 *
 * @param {StrategyEntity} entity - The entity being compared
 * @param {Team[]} teams - Team list forwarded to the name resolution
 * @returns {string} Comma-joined lowercased team names, or '' when the entity has none
 */
const getTeamsSortLabel = (entity: StrategyEntity, teams: Team[] = []): string => {
  const names = getEntityTeamNames(entity, teams);

  if (names.length > 0) {
    return names.join(', ').toLowerCase();
  }

  // InitiativeRecord rows arrive with a pre-derived label instead of raw team data.
  if ('contributing_teams_label' in entity && typeof entity.contributing_teams_label === 'string') {
    return entity.contributing_teams_label.toLowerCase();
  }

  return '';
};

/**
 * Custom sort function for strategy entities
 *
 * @param {StrategyEntity[]} data - The array of entities to sort
 * @param {string} sortBy - The field to sort by
 * @param {'asc' | 'desc'} direction - The sort direction
 * @param {Team[]} teams - Team list used to resolve names for the Teams column sort
 * @returns {StrategyEntity[]} A new sorted array
 */
const sortStrategyEntities = (
  data: StrategyEntity[],
  sortBy: string,
  direction: 'asc' | 'desc',
  teams: Team[] = [],
): StrategyEntity[] => {
  return [...data].sort((a, b) => {
    const multiplier = direction === 'asc' ? 1 : -1;

    switch (sortBy) {
      case 'title':
      case 'key': {
        const aKey = String((a as any).key || (a as any).title || '');
        const bKey = String((b as any).key || (b as any).title || '');

        // Extract prefix and numeric parts
        const aNumeric = extractNumericId(aKey);
        const bNumeric = extractNumericId(bKey);

        // First compare by prefix (e.g., "OBJ-" vs "INI-")
        const aPrefix = aKey.replace(/\d+$/, '');
        const bPrefix = bKey.replace(/\d+$/, '');

        if (aPrefix !== bPrefix) {
          return multiplier * aPrefix.localeCompare(bPrefix);
        }

        // Then compare by numeric value
        return multiplier * (aNumeric - bNumeric);
      }

      case 'name': {
        const aName = stripEmojis(String(a.name || ''));
        const bName = stripEmojis(String(b.name || ''));
        return multiplier * aName.localeCompare(bName);
      }

      case 'percent_progress': {
        // Handle both regular strategy entities and epics
        let aProgress = (a as any).percent_progress;
        let bProgress = (b as any).percent_progress;

        // For epics, calculate completion from subtasks
        if (aProgress === undefined && (a as any).subtasks_count !== undefined) {
          const aCompletedCount = (a as any).subtasks_completed_count ?? 0;
          aProgress =
            (a as any).subtasks_count > 0 ? Math.round((aCompletedCount / (a as any).subtasks_count) * 100) : 0;
        }
        if (bProgress === undefined && (b as any).subtasks_count !== undefined) {
          const bCompletedCount = (b as any).subtasks_completed_count ?? 0;
          bProgress =
            (b as any).subtasks_count > 0 ? Math.round((bCompletedCount / (b as any).subtasks_count) * 100) : 0;
        }

        aProgress = aProgress || 0;
        bProgress = bProgress || 0;
        return multiplier * (aProgress - bProgress);
      }

      case 'end_date': {
        const aHasDate = !!(a as any).end_date;
        const bHasDate = !!(b as any).end_date;

        // Always put null dates at the bottom
        if (!aHasDate && !bHasDate) return 0;
        if (!aHasDate) return 1;
        if (!bHasDate) return -1;

        // Both have dates, sort normally
        const aDate = new Date((a as any).end_date).getTime();
        const bDate = new Date((b as any).end_date).getTime();
        return multiplier * (aDate - bDate);
      }

      case 'created_date': {
        const aHasDate = !!(a as any).created_date;
        const bHasDate = !!(b as any).created_date;

        // Always put null dates at the bottom
        if (!aHasDate && !bHasDate) return 0;
        if (!aHasDate) return 1;
        if (!bHasDate) return -1;

        // Both have dates, sort normally
        const aDate = new Date((a as any).created_date).getTime();
        const bDate = new Date((b as any).created_date).getTime();
        return multiplier * (aDate - bDate);
      }

      case 'due_date': {
        const aHasDate = !!(a as any).due_date;
        const bHasDate = !!(b as any).due_date;

        // Always put null dates at the bottom
        if (!aHasDate && !bHasDate) return 0;
        if (!aHasDate) return 1;
        if (!bHasDate) return -1;

        // Both have dates, sort normally
        const aDate = new Date((a as any).due_date).getTime();
        const bDate = new Date((b as any).due_date).getTime();
        return multiplier * (aDate - bDate);
      }

      case 'contributing_teams_label': {
        const aTeams = getTeamsSortLabel(a, teams);
        const bTeams = getTeamsSortLabel(b, teams);

        // Always put entities with no contributing teams at the bottom
        if (!aTeams && !bTeams) return 0;
        if (!aTeams) return 1;
        if (!bTeams) return -1;

        return multiplier * aTeams.localeCompare(bTeams);
      }

      case 'costs_to_date': {
        const aCost = (a as any).costs_to_date?.initiative || 0;
        const bCost = (b as any).costs_to_date?.initiative || 0;
        return multiplier * (aCost - bCost);
      }

      case 'percent_progress_tasks': {
        const aProgress = (a as any).percent_progress_tasks ?? 0;
        const bProgress = (b as any).percent_progress_tasks ?? 0;
        return multiplier * (aProgress - bProgress);
      }

      case 'scope_increase_tasks_3m': {
        const aScope = (a as any).scope_increase_tasks_3m ?? 0;
        const bScope = (b as any).scope_increase_tasks_3m ?? 0;
        return multiplier * (aScope - bScope);
      }

      default:
        return 0;
    }
  });
};

export { getEntityTeamNames, getSourceIcon, getStatusColor, getTeamsSortLabel, sortStrategyEntities };
